/**
 * TanStack Query hooks for `sftp-scheduled-export`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	create,
	list,
	remove,
	runNow,
	sftpScheduledExportQueryKeys,
	testConnection,
	type CreateSftpScheduledExportBody,
	type ListSftpScheduledExportResponse,
	type RunNowResult,
	type SftpScheduledExportRow,
	type TestConnectionResult,
} from "./api";

export function useSftpScheduledExportList(): UseQueryResult<ListSftpScheduledExportResponse, Error> {
	return useQuery({
		queryKey: sftpScheduledExportQueryKeys.list(),
		queryFn: list,
	});
}

export function useSftpScheduledExportCreate(): UseMutationResult<SftpScheduledExportRow, Error, CreateSftpScheduledExportBody> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: create,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: sftpScheduledExportQueryKeys.all });
		},
	});
}

export function useSftpScheduledExportDelete(): UseMutationResult<{ removed: boolean }, Error, string> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: remove,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: sftpScheduledExportQueryKeys.all });
		},
	});
}

export function useSftpScheduledExportRunNow(): UseMutationResult<RunNowResult, Error, string> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: runNow,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: sftpScheduledExportQueryKeys.all });
		},
	});
}

export function useSftpScheduledExportTestConnection(): UseMutationResult<
	TestConnectionResult,
	Error,
	Parameters<typeof testConnection>[0]
> {
	return useMutation({
		mutationFn: testConnection,
	});
}
